/**
 * ContactDialog — "Fale conosco" (porta de `showContact` [application.js:7280]), no visual da
 * proposta "LeadExplorer & Contact" (17/10/2026): diz para qual email vai a resposta, conta os
 * caracteres perto do limite (1000), mostra "Enviando…" e "Mensagem enviada", mantém o texto se
 * o envio falhar e pergunta antes de descartar uma mensagem escrita.
 * O assunto é obrigatório (pedido do Marcelo, 18/10/2026) e vai no título do email da equipe.
 * POST /app/contact/ { subject, message } — o usuário é identificado pelos headers.
 *
 * Movimento (23/10/2026): a janela entra e sai com .ds-veu/.ds-dialogo (quem abre desmonta ao
 * fechar, então a saída toca antes do onClose); "Enviar mensagem" acende e solta o halo uma vez
 * quando assunto e texto ficam prontos (M12 b) e, passados 200 ms, troca para o giro com
 * "Enviando…" (aria-disabled enquanto envia: continua focável e o clique não faz nada); o erro e
 * "Mensagem enviada" abrem espaço no lugar (.ds-altura), com o ✓ entrando com .ds-sucesso; o
 * contador entra com fade perto do limite e não é anunciado tecla a tecla, só ao chegar ao limite.
 */
import { useRef, useState } from 'react';
import { useContact } from '../../api/useFeedback';
import { useBrand } from '../../hooks/useBrand';
import { useSessionStore } from '../../stores/sessionStore';
import { externalUrls } from '../../config/env';
import { emitActivity } from '../../lib/activity';
import { AbreNoLugar, Html, RotuloCarregando, ScreenDialog } from '../../components/screen/Screen';
import { escapeHtml, useFecharComSaida, useHaloUmaVez } from '../../components/screen/screenText';
import { useLxText, type LxKey } from '../leadexplorer/leText';
import './contact.css';

interface ContactDialogProps {
  onClose: () => void;
  /** Assunto já escolhido ("Preciso de ajuda" do guia passo a passo abre em "problem"). */
  initialSubject?: ContactSubject;
}

/** Assuntos do "Fale conosco"; o backend conhece os mesmos ids (CONTACT_SUBJECTS em app.js). */
export const CONTACT_SUBJECTS = [
  'access',
  'users',
  'searches',
  'renewal',
  'billing',
  'cancel',
  'problem',
  'feature',
  'feedback',
  'referral',
  'other',
] as const;
export type ContactSubject = (typeof CONTACT_SUBJECTS)[number];

const LIMIT = 1000;
/** O contador aparece quando faltam menos de 200 caracteres. */
const COUNTER_FROM = 800;

export function ContactDialog({ onClose, initialSubject }: ContactDialogProps): JSX.Element {
  const { tx } = useLxText();
  const brand = useBrand();
  const email = useSessionStore((s) => s.session?.email ?? '');
  const contact = useContact();
  const [subject, setSubject] = useState<ContactSubject | ''>(initialSubject ?? '');
  const [message, setMessage] = useState('');
  const [failed, setFailed] = useState(false);
  const [sent, setSent] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const help =
    brand.source === 'lead-machine' ? externalUrls.helpLeadMachine : externalUrls.helpEminer;

  const sending = contact.isPending;
  /** Assunto e texto prontos: sem isso o botão fica desativado (falta algo). */
  const ready = subject !== '' && message.trim().length > 0 && !sent;
  const canSubmit = ready && !sending;
  const halo = useHaloUmaVez(canSubmit);
  const { aberta, fechar, fecharDepois } = useFecharComSaida(onClose);

  function requestClose(): void {
    if (sending) return;
    if (message.trim() && !sent) {
      setDiscarding(true);
      return;
    }
    fechar();
  }

  function submit(): void {
    if (!canSubmit) return;
    setFailed(false);
    contact.mutate(
      { subject, message: message.trim() },
      {
        onSuccess: () => {
          emitActivity('contact.sent', { subject: String(subject).slice(0, 40) });
          setSent(true);
          // A confirmação fica à vista por 4 s (o tempo do toast do guia) ou até "Fechar". O prazo
          // é cancelado por "Fechar" e ao desmontar: não fecha um "Fale conosco" aberto depois.
          fecharDepois(4000);
        },
        onError: () => {
          setFailed(true);
          fieldRef.current?.focus();
        },
      },
    );
  }

  return (
    <ScreenDialog
      open={aberta}
      icon="chat"
      title={tx('ct_title')}
      subtitle={
        email ? (
          <Html as="span" html={tx('ct_text', { email: escapeHtml(email) })} />
        ) : (
          tx('ct_text_no_email')
        )
      }
      className="ct-dialog"
      closeLabel={tx('close')}
      closeDisabled={sending}
      onClose={requestClose}
    >
      <AbreNoLugar aberto={failed}>
        <div className="as-note as-note--error" role="alert">
          <span className="material-symbols-rounded" aria-hidden="true">
            error
          </span>
          <div>{tx('ct_error')}</div>
        </div>
      </AbreNoLugar>
      <div className="as-field">
        <label htmlFor="ct-subject" className="as-label">
          {tx('ct_subject')}
        </label>
        <div className="as-select ct-subject">
          <select
            id="ct-subject"
            value={subject}
            autoFocus
            required
            disabled={sending || sent}
            className={`ds-campo${subject ? '' : ' is-empty'}`}
            onChange={(e) => {
              setSubject(e.target.value as ContactSubject);
              fieldRef.current?.focus();
            }}
          >
            <option value="" disabled>
              {tx('ct_subject_placeholder')}
            </option>
            {CONTACT_SUBJECTS.map((id) => (
              <option key={id} value={id}>
                {tx(`ct_s_${id}` as LxKey)}
              </option>
            ))}
          </select>
          <span className="material-symbols-rounded" aria-hidden="true">
            expand_more
          </span>
        </div>
      </div>
      <div className="as-field">
        {/* Rótulo sempre à vista (guia/07-campos); o placeholder fica só com o exemplo. */}
        <label htmlFor="ct-message" className="as-label">
          {tx('ct_label')}
        </label>
        <textarea
          ref={fieldRef}
          id="ct-message"
          className="as-input ct-text ds-campo"
          maxLength={LIMIT}
          rows={9}
          placeholder={tx('ct_placeholder')}
          value={message}
          disabled={sending || sent}
          aria-describedby={message.length >= COUNTER_FROM ? 'ct-count' : undefined}
          onChange={(e) => {
            setMessage(e.target.value);
            setDiscarding(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
        />
        {/* Sem aria-live: a contagem não é anunciada tecla a tecla; o campo a lê pelo describedby. */}
        {message.length >= COUNTER_FROM ? (
          <span
            id="ct-count"
            className={`ct-count ds-aparece${message.length >= LIMIT ? ' is-full' : ''}`}
          >
            {message.length}/{LIMIT}
          </span>
        ) : null}
        {/* Chegar ao limite é anunciado uma vez (a região fica sempre montada, vazia antes). */}
        <span className="as-sr" aria-live="polite">
          {message.length >= LIMIT ? `${LIMIT}/${LIMIT}` : ''}
        </span>
      </div>

      {/* Confirmação como mensagem de sucesso do guia, não como botão verde (decisão D19). */}
      <AbreNoLugar aberto={sent}>
        <div className="as-note as-note--success ct-sent" role="status">
          <span className="material-symbols-rounded ds-sucesso" aria-hidden="true">
            check_circle
          </span>
          <div>{tx('ct_sent_note')}</div>
        </div>
      </AbreNoLugar>
      {discarding ? (
        <div
          className="ct-confirm ds-aparece"
          role="alertdialog"
          aria-labelledby="ct-discard-title"
        >
          <strong id="ct-discard-title">{tx('ct_discard_title')}</strong>
          <p>{tx('ct_discard_text')}</p>
          <div className="as-dialog__actions">
            <button
              type="button"
              className="as-btn as-btn--outline as-btn--md ds-resposta"
              onClick={() => {
                setDiscarding(false);
                fieldRef.current?.focus();
              }}
            >
              {tx('ct_keep')}
            </button>
            <button
              type="button"
              className="as-btn as-btn--dark as-btn--md ds-resposta"
              onClick={fechar}
            >
              {tx('ct_discard')}
            </button>
          </div>
        </div>
      ) : (
        <div className="ct-foot">
          <a className="ct-help" href={help} target="_blank" rel="noopener noreferrer">
            <span className="material-symbols-rounded" aria-hidden="true">
              help
            </span>
            {tx('ct_help')}
          </a>
          {sent ? (
            <button
              type="button"
              className="as-btn as-btn--md as-btn--outline ds-resposta"
              onClick={fechar}
            >
              {tx('close')}
            </button>
          ) : (
            <button
              type="button"
              id="send"
              className={`as-btn as-btn--md as-btn--primary ds-resposta${halo ? ' ds-pulso-acao' : ''}`}
              disabled={!ready}
              aria-disabled={sending || undefined}
              aria-busy={sending || undefined}
              onClick={submit}
            >
              <RotuloCarregando
                ocupado={sending}
                icone="send"
                rotulo={failed ? tx('ct_retry') : tx('ct_send')}
                rotuloOcupado={tx('ct_sending')}
              />
            </button>
          )}
        </div>
      )}
    </ScreenDialog>
  );
}
